import React, { useContext, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { Grid, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { SectionGridContainer } from "../../components/Layout";
import { RiderContext } from "../../context";
import { StringUtils } from "../../util";
import { KeyValueDisplay } from "../../components/Typography";
import { useTestIds } from "../../hooks/useTestIds";

const useStyles = makeStyles(() => ({
  divider: {
    paddingLeft: 0,
    paddingRight: 0,
    height: 10,
  },
  manageButton: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 0,
  },
}));

const Devices = () => {
  const { t } = useTranslation();
  const { riderId } = useParams();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const testIds = useTestIds("rider-profile-devices-widget");

  const { activeRider, userDevices, reservations } = useContext(RiderContext);

  const [lastActiveDevice, setLastActiveDevice] = useState(null);
  const [lastUserReservation, setLastUserReservation] = useState(null);
  const [lastActiveDate, setLastActiveDate] = useState("");

  useEffect(() => {
    if (userDevices) {
      const lastDeviceUsed = findLastActiveDevice(userDevices);
      setLastActiveDevice(lastDeviceUsed);
    }
  }, [location, activeRider, lastUserReservation, userDevices]);

  useEffect(() => {
    const waitForActiveDate = () => {
      const latestUserRes = reservations.data.find(
        (res) => StringUtils.uuidFromUrl(res.rider) === riderId,
      );
      const lastUserActivity = DateTime.fromJSDate(latestUserRes?.start).toFormat("LLL dd yyyy");
      setLastUserReservation(latestUserRes);
      setLastActiveDate(lastUserActivity);
    };

    if (reservations?.data?.length) {
      waitForActiveDate();
    } else {
      setLastActiveDate(t("Riders.Wallet.Devices.NotAvailable"));
    }
  }, [reservations, lastActiveDate, lastUserReservation]);

  const findLastActiveDevice = (devices) =>
    devices.find((device) => {
      const udUserId = device.users
        .map((user) => StringUtils.uuidFromUrl(user.url))
        .find((id) => riderId === id);

      if (lastUserReservation) {
        const resUserId = StringUtils.uuidFromUrl(lastUserReservation.rider);
        return udUserId === resUserId;
      }

      return false;
    });

  const openDeviceModal = () =>
    history.push(`/riders/${riderId}/manage-devices`, {
      background: location,
    });

  return (
    <SectionGridContainer
      spaceContents
      gridProps={{
        "data-testid": testIds.wrapper,
      }}
    >
      <KeyValueDisplay
        label={t("Riders.Wallet.Devices.Title")}
        keyTypographyProps={{ variant: "h5" }}
        separator=""
        testIds={{ label: testIds.title }}
      />
      <KeyValueDisplay
        label={t("Riders.Wallet.Devices.Last")}
        keyTypographyProps={{ variant: "h6" }}
        separator=""
        testIds={{ label: testIds.subtitle }}
      />
      {lastActiveDevice && (
        <>
          <KeyValueDisplay
            label={t("Riders.Wallet.Devices.Type")}
            text={lastActiveDevice.model}
            testIds={{
              label: testIds.lastDeviceTypeLabel,
              text: testIds.lastDeviceTypeValue,
            }}
            labelMinWidth="50%"
          />
          <KeyValueDisplay
            label={t("Riders.Wallet.Devices.OS")}
            text={lastActiveDevice.os_name}
            testIds={{
              label: testIds.lastDeviceOsLabel,
              text: testIds.lastDeviceOsValue,
            }}
            labelMinWidth="50%"
          />
          <KeyValueDisplay
            label={t("Riders.Wallet.Devices.Version")}
            text={lastActiveDevice.app_version}
            testIds={{
              label: testIds.lastDeviceVersionLabel,
              text: testIds.lastDeviceVersionValue,
            }}
            labelMinWidth="50%"
          />
          <KeyValueDisplay
            label={t("Riders.Wallet.Devices.Date")}
            text={lastActiveDate}
            testIds={{
              label: testIds.lastActiveDateLabel,
              text: testIds.lastActiveDateValue,
            }}
            labelMinWidth="50%"
          />
        </>
      )}
      <Grid item xs={12}>
        <Button
          className={classes.manageButton}
          onClick={openDeviceModal}
          data-testid={testIds.manageDevicesButton}
        >
          {t("Riders.Wallet.Devices.Manage")}
        </Button>
      </Grid>
    </SectionGridContainer>
  );
};

export default Devices;
